import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, User, Eye, EyeOff } from 'lucide-react';
import { useAuthStore } from '../../store/useAuthStore';
import bremskerl1 from '../../assets/images/bremskerl_1.avif'; // Assuming the image is correctly imported
import { toast } from 'react-toastify'; // Import toast
import 'react-toastify/dist/ReactToastify.css'; // Import toast styles
import ConfirmPasswordPopup from '../../components/Model/ConfirmPassword';
import ForgotPasswordPopup from '../../components/Model/ForgotPasswordPopup';
import EnterOtp from '../../components/Model/EnterOtp';
import LogoutConfirmation from '../../components/Model/LogoutConfirmation';

const Login = () => {
  const [formData, setFormData] = useState({
    mobile: '',
    password: ''
  });
  const [showFpPopup,setShowFpPopup] = useState(false)
  const [showResetPopup, setShowResetPopup] = useState(false);
  const [showOtpPopup, setShowOtpPopup] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  
  const navigate = useNavigate();
  const { login } = useAuthStore();
  const [userId,setUserId] = useState();
  const [showConfirmationPopup,setShowConfirmation] = useState(false)
  const [msg,setMsg] = useState()

  const onClose = () => {
    setShowConfirmation(false)
  }

  const onConfirm = async () => {
    const { mobile, password } = formData;
    const {status,message} = await login({mobile,password,override:true})
    if (status === true) {
      toast.success(message, {toastId: 'successMessageConfirm'});
      navigate('/');
    } else {
      toast.error(message, { toastId: 'errorMessageConfirm' });
    }
  }

  // Handle the input change for both fields
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === 'mobile') {
      // Allow only numeric characters and limit to 10 digits
      const numericValue = value.replace(/[^0-9]/g, ''); // Replace non-numeric characters
      if (numericValue.length <= 10) {
        setFormData({
          ...formData,
          [name]: numericValue
        });
      }
    } else {
      // For password and other fields, update normally
      setFormData({
        ...formData,
        [name]: value
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const { mobile, password } = formData;

    // Validate if fields are empty
    if (mobile === '' || password === '') {
      toast.error('Please fill in both fields', { toastId: 'emptyFieldError' });
      return;
    }

    // Mobile validation (basic 10-digit phone number)
    const mobileRegex = /^[0-9]{10}$/;
    if (!mobileRegex.test(mobile)) {
      toast.error('Please enter a valid mobile number', { toastId: 'mobileNumberError' });
      return;
    }

    // Simulate login
    const {status, message} = await login({ mobile, password });

    if(status === 409) {
      setMsg(message)
      setShowConfirmation(true)
    } else if (status === true) {
      toast.success(message, {toastId: 'successMessageLogin'});
      navigate('/');
    } else {
      toast.error(message, { toastId: 'errorMessageLogin' });
    }
  };

  const enableResetPopup = () => { 
    setShowResetPopup(true)
    setShowOtpPopup(false)
    setShowFpPopup(false)
  }

  const enableOtpPopup = () => {
    setShowOtpPopup(true)
    setShowFpPopup(false)
  }

  const resetOtpPopup = () => {
    setShowOtpPopup(false)
    setShowFpPopup(true)
  }

  return (
    <div className="min-h-screen flex">
      {/* Left Panel - Login Form */}
      <div className="w-full md:w-2/3 bg-white p-8 flex flex-col justify-center">
        <div className="max-w-md mx-auto w-full space-y-8">
          <div className="text-center">
            <img
              src={'/bremskerl_logo_mobile.png'}
              alt="Brake System"
              className="w-10 h-10 object-contain inline-block"
            />
            <h1 className="text-4xl font-bold text-gray-900 tracking-tight">BREMSKERL</h1>
            <div className="h-1 w-24 bg-emerald-500 mx-auto mt-2"></div>
          </div>

          <div className="bg-white p-8 rounded-lg shadow-lg">
            <h2 className="text-2xl font-semibold text-gray-800 mb-6">Welcome Back!</h2>

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Mobile Input */}
              <div>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                  <input
                    type="text"
                    name="mobile"
                    value={formData.mobile}
                    onChange={handleInputChange}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    placeholder="Mobile No"
                    maxLength={10} // Restrict to 10 digits
                    
                  />
                </div>
              </div>

              {/* Password Input */}
              <div>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                  <input
                    type={showPassword ? "text" : "password"}
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    placeholder="Password"
                    
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 focus:outline-none"
                  >
                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                className="w-full bg-emerald-500 text-white py-2 px-4 rounded-lg hover:bg-emerald-600 transition duration-200"
              >
                Log In
              </button>
              <p className="text-center mt-2" style={{cursor: 'pointer'}} onClick={() => setShowFpPopup(true)}>Forgot Password ?</p>
                {
                  showFpPopup && <ForgotPasswordPopup onClose={() => setShowFpPopup(false)} enablePopup={enableOtpPopup} setUserId={setUserId} />
                }
                {
                  showOtpPopup && <EnterOtp onClose={() => setShowOtpPopup(false)} enablePopup={enableResetPopup} resetOtpPopup={resetOtpPopup} userId={userId} />
                }
                {
                  showResetPopup && <ConfirmPasswordPopup onClose={() => setShowResetPopup(false)} enableOtpPopup={enableOtpPopup} userId={userId} />
                }
            </form>
          </div>
        </div>
      </div>

      {/* Right Panel - Image (hidden on mobile) */}
      <div className="hidden md:block w-full mt-[1%] mr-5">
        <div className="h-full relative">
          <img
            src={bremskerl1}
            alt="Brake System"
            className="w-full h-[80%] mt-5"
          />
        </div>
      </div>
        {showConfirmationPopup && <LogoutConfirmation message={msg} onCancel={onClose} onConfirm={onConfirm} />}
    </div>
  );
};

export default Login;